import { Component, OnDestroy, OnInit } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { UploadManagerService } from '#services/upload-manager/upload-manager.service';
import { UploadMediaItem, UploadStatus } from '#models/db/asset/upload.model';
import { formatFileSize } from '#helpers/upload-validation.helper';

@Component({
  selector: 'app-upload-manager',
  templateUrl: './upload-manager.component.html',
  styleUrls: ['./upload-manager.component.scss'],
  standalone: true,
  imports: [IonicModule],
})
export class UploadManagerComponent implements OnInit, OnDestroy {
  uploads: UploadMediaItem[] = [];
  isProcessing = false;
  expanded = false;
  visible = false;
  cancellingIds = new Set<number>();

  private dismissTimeout: ReturnType<typeof setTimeout> | null = null;
  private subscription = new Subscription();

  readonly UploadStatus = UploadStatus;
  readonly Math = Math;

  get completedCount(): number {
    return this.uploads.filter((u) => u.status === UploadStatus.Completed).length;
  }

  get totalCount(): number {
    return this.uploads.length;
  }

  get failedCount(): number {
    return this.uploads.filter((u) => u.status === UploadStatus.Failed).length;
  }

  get overallProgress(): number {
    if (this.totalCount === 0) return 0;

    const totalBytes = this.uploads.reduce((acc, u) => acc + (u.fileSize ?? u.file.size), 0);
    const uploadedBytes = this.uploads.reduce(
      (acc, u) => acc + (u.progress ?? 0) * (u.fileSize ?? u.file.size),
      0
    );

    return totalBytes > 0 ? uploadedBytes / totalBytes : 0;
  }

  get allComplete(): boolean {
    return (
      this.uploads.length > 0 &&
      this.uploads.every(
        (u) =>
          u.status === UploadStatus.Completed ||
          u.status === UploadStatus.Failed ||
          u.status === UploadStatus.Cancelled
      )
    );
  }

  get statusText(): string {
    if (this.allComplete) {
      if (this.failedCount > 0) {
        return `${this.completedCount} of ${this.totalCount} uploaded (${this.failedCount} failed)`;
      }
      return `${this.completedCount} of ${this.totalCount} uploaded`;
    }
    return `Uploading ${this.completedCount} of ${this.totalCount}...`;
  }

  constructor(private uploadManager: UploadManagerService) {}

  ngOnInit() {
    this.subscription.add(
      this.uploadManager.uploads$.subscribe((uploads) => {
        this.uploads = uploads;

        if (uploads.length > 0) {
          this.visible = true;
          this.clearDismissTimeout();
        }
      })
    );

    this.subscription.add(
      this.uploadManager.isProcessing$.subscribe((processing) => {
        this.isProcessing = processing;

        if (!processing && this.uploads.length > 0) {
          this.expanded = false;
          this.scheduleDismiss();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.clearDismissTimeout();
  }

  toggleExpanded() {
    if (this.allComplete) return;
    this.expanded = !this.expanded;
  }

  dismiss() {
    this.visible = false;
    this.expanded = false;
    this.cancellingIds.clear();
    this.uploadManager.clearUploads();
  }

  retryAllFailed() {
    this.uploadManager.retryAllFailed();
  }

  retryUpload(item: UploadMediaItem) {
    if (item.id) {
      this.uploadManager.retryUpload(item.id);
    }
  }

  cancelUpload(item: UploadMediaItem) {
    if (!item.id || this.cancellingIds.has(item.id)) {
      return;
    }
    this.cancellingIds.add(item.id);
    this.uploadManager.cancelUpload(item.id);
  }

  removeUpload(item: UploadMediaItem) {
    if (item.id) {
      this.uploadManager.removeUpload(item.id);
    }
  }

  getStatusIcon(status: UploadStatus | undefined): string {
    switch (status) {
      case UploadStatus.Completed:
        return 'checkmark-circle-outline';
      case UploadStatus.Failed:
        return 'alert-circle-outline';
      case UploadStatus.Uploading:
        return 'cloud-upload-outline';
      case UploadStatus.Cancelled:
        return 'close-circle-outline';
      default:
        return 'time-outline';
    }
  }

  getStatusColor(status: UploadStatus | undefined): string {
    switch (status) {
      case UploadStatus.Completed:
        return 'success';
      case UploadStatus.Failed:
        return 'danger';
      case UploadStatus.Uploading:
        return 'primary';
      case UploadStatus.Cancelled:
        return 'medium';
      default:
        return 'medium';
    }
  }

  getStatusLabel(status: UploadStatus | undefined): string {
    switch (status) {
      case UploadStatus.Completed:
        return 'Completed';
      case UploadStatus.Failed:
        return 'Failed';
      case UploadStatus.Uploading:
        return 'Uploading';
      case UploadStatus.Cancelled:
        return 'Cancelled';
      default:
        return 'Pending';
    }
  }

  formatSize(bytes: number): string {
    return formatFileSize(bytes);
  }

  private scheduleDismiss() {
    this.clearDismissTimeout();

    if (this.failedCount === 0) {
      this.dismissTimeout = setTimeout(() => {
        this.dismiss();
      }, 10000);
    }
  }

  private clearDismissTimeout() {
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout);
      this.dismissTimeout = null;
    }
  }
}
